"use client";
import type { LinkComponentType } from "../../../index";
import type { DeviceType } from "../../../types/DeviceTypes";
import { DeviceMetaDisplay } from "../../DeviceMetaDisplay";
import type { CellComponentProps } from "../DataTable.types";
import { getCellContainerClasses, resolveValue } from "./cellUtils";

export interface DeviceMetaCellProps<T = unknown> extends CellComponentProps<T> {
  /** Device manufacturer name (can be a function to extract from row) */
  manufacturer: string | ((row: T) => string);

  /** Device model name (can be a function to extract from row) */
  model: string | ((row: T) => string);

  /** Device type for fallback icon (can be a function to extract from row) */
  deviceType: DeviceType | ((row: T) => DeviceType);

  /** URL to manufacturer logo (optional, can be a function to extract from row) */
  logoUrl?: string | ((row: T) => string | undefined);

  /** Layout variant - stacked (vertical) or inline (horizontal) */
  layout?: "stacked" | "inline";

  /** Size variant */
  size?: "xs" | "sm" | "md" | "lg";

  /** Link to device detail page (optional) */
  href?: string | ((row: T) => string);

  /** Custom link component (e.g., Next.js Link) */
  LinkComponent?: LinkComponentType;

  /** Link variant for the manufacturer name */
  linkVariant?: "primary" | "secondary" | "muted" | "unstyled" | "default" | "brand";

  /** Show device type icon next to model name (stacked) or hide logo (inline) */
  showDeviceTypeIcon?: boolean;

  /** Show logo in stacked layout (can be hidden for compact view) */
  showLogo?: boolean;

  /** Alignment */
  align?: "left" | "center" | "right";

  /** Additional classes */
  className?: string;

  /**
   * Entity identifier for PII masking (can be a function to extract from row)
   * Example: (row) => `device:${row.id}`
   */
  piiEntity?: string | ((row: T) => string | undefined);
}

/**
 * DeviceMetaCell
 *
 * DataTable cell component for displaying device metadata (manufacturer, model, logo).
 * Wraps DeviceMetaDisplay for use in DataTable columns with layout="stacked" by default.
 *
 * @example
 * ```tsx
 * // In DataTable column definition
 * {
 *   id: "device",
 *   label: "Device",
 *   cell: DeviceMetaCell,
 *   cellProps: {
 *     manufacturer: (row) => row.manufacturerProfile?.name || row.manufacturer,
 *     model: (row) => row.displayModelName || row.model,
 *     deviceType: (row) => row.type,
 *     logoUrl: (row) => row.manufacturerProfile?.logo,
 *     href: (row) => `/devices/${row.id}`,
 *     layout: "stacked",
 *     size: "sm"
 *   }
 * }
 * ```
 */
export function DeviceMetaCell<T = unknown>({
  row,
  context,
  manufacturer,
  model,
  deviceType,
  logoUrl,
  layout = "stacked",
  size = "sm",
  href,
  LinkComponent,
  linkVariant = "secondary",
  showDeviceTypeIcon = true,
  showLogo = true,
  emphasis = "normal",
  align = "left",
  className,
  piiEntity,
}: DeviceMetaCellProps<T>) {
  const { isLoading } = context;

  // Resolve dynamic values
  const manufacturerName = resolveValue(manufacturer, row);
  const modelName = resolveValue(model, row);
  const deviceTypeValue = resolveValue(deviceType, row);
  const logoUrlValue = resolveValue(logoUrl, row);
  const hrefValue = resolveValue(href, row);
  const piiEntityValue = resolveValue(piiEntity, row);

  const containerClasses = getCellContainerClasses(align, className);

  return (
    <div className={containerClasses}>
      <DeviceMetaDisplay
        manufacturer={manufacturerName}
        model={modelName}
        deviceType={deviceTypeValue}
        logoUrl={logoUrlValue}
        layout={layout}
        size={size}
        href={hrefValue}
        LinkComponent={LinkComponent}
        linkVariant={linkVariant}
        showDeviceTypeIcon={showDeviceTypeIcon}
        showLogo={showLogo}
        emphasis={emphasis}
        isLoading={isLoading}
        piiEntity={piiEntityValue}
      />
    </div>
  );
}
